/**
 * CR229: Admin Diagnostics Panel
 * CR315: Extended with visibility stats breakdown
 * 
 * Collapsible panel showing live deal count, snapshot info, visibility breakdown, and debug JSON.
 * Helps debug visibility issues between admin UI and public site.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  ChevronDown, 
  ChevronUp, 
  Copy, 
  RefreshCw, 
  Check,
  AlertTriangle,
  CheckCircle2,
  Database,
  Globe,
  Clock,
} from "lucide-react";
import type { SiteState, PublishAuditEntry } from "@/hooks/useSiteState";
import type { VisibilityStats, RetentionPolicyMode } from "@/lib/deals/publicVisibility";
import { VisibilityStatsBreakdown } from "@/components/admin/production-tools/VisibilityStatsBreakdown";
import { toast } from "sonner";

interface AdminDiagnosticsPanelProps {
  siteState: SiteState | null;
  publishHistory: PublishAuditEntry[];
  adminLiveDealsCount: number;
  publicDealsCount?: number;
  visibilityStats?: VisibilityStats | null;
  loading?: boolean;
  onRefresh: () => void;
}

export function AdminDiagnosticsPanel({
  siteState,
  publishHistory,
  adminLiveDealsCount,
  publicDealsCount,
  visibilityStats,
  loading = false,
  onRefresh,
}: AdminDiagnosticsPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString("en-CA", {
      timeZone: "America/Toronto",
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Build debug JSON - CR315: Include visibility stats
  const debugJson = {
    timestamp: new Date().toISOString(),
    site_state: {
      active_snapshot_id: siteState?.active_snapshot_id || null,
      active_batch_run_id: siteState?.active_batch_run_id || null,
      published_at: siteState?.published_at || null,
      published_by: siteState?.published_by || null,
      published_deal_count: siteState?.published_deal_count || null,
      admin_snapshot_id: siteState?.admin_snapshot_id || null,
      admin_batch_run_id: siteState?.admin_batch_run_id || null,
      admin_deal_count: siteState?.admin_deal_count || null,
      policy_mode: siteState?.public_deals_policy_mode || null,
      grace_days: siteState?.public_deals_policy_grace_days || null,
    },
    admin_ui_counts: {
      live_deals_shown: adminLiveDealsCount,
      public_deals_count: publicDealsCount ?? "not fetched",
    },
    // CR315: Include visibility breakdown in debug JSON
    visibility_stats: visibilityStats || null,
    last_publish: publishHistory[0] || null,
    sync_status: {
      admin_matches_live: siteState?.admin_snapshot_id === siteState?.active_snapshot_id,
      count_mismatch: publicDealsCount !== undefined && publicDealsCount !== adminLiveDealsCount,
    },
  };

  const handleCopyDebugJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(debugJson, null, 2));
      setCopied(true);
      toast.success("Debug JSON copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  // Determine sync status
  const isSynced = siteState?.admin_snapshot_id === siteState?.active_snapshot_id;
  const hasCountMismatch = publicDealsCount !== undefined && publicDealsCount !== adminLiveDealsCount;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="border rounded-lg bg-muted/30"
    >
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full flex items-center justify-between p-4 h-auto hover:bg-muted/50"
        >
          <div className="flex items-center gap-3">
            <Database className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm">CR229 Diagnostics</span>
            
            {/* Quick status indicators */}
            <div className="flex items-center gap-2">
              {siteState?.active_snapshot_id ? (
                <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-500/30 text-xs">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Live: {adminLiveDealsCount} deals
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-red-500/10 text-red-700 border-red-500/30 text-xs">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  No Live Snapshot
                </Badge>
              )}
              
              {hasCountMismatch && (
                <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30 text-xs">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Count Mismatch
                </Badge>
              )}
            </div>
          </div>
          
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-4 pb-4 space-y-4">
          {/* Sync Status Alert */}
          {!isSynced && siteState?.admin_snapshot_id && (
            <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-700">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">
                Admin snapshot differs from Live. Publish to sync.
              </span>
            </div>
          )}

          {/* Main Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Active Snapshot */}
            <div className="p-3 bg-background rounded-lg border">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Globe className="h-3 w-3" />
                Active Snapshot
              </div>
              <div className="font-mono text-xs truncate" title={siteState?.active_snapshot_id || "None"}>
                {siteState?.active_snapshot_id?.slice(0, 20) || "None"}
                {siteState?.active_snapshot_id && siteState.active_snapshot_id.length > 20 && "..."}
              </div>
            </div>

            {/* Last Published */}
            <div className="p-3 bg-background rounded-lg border">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Clock className="h-3 w-3" />
                Last Published
              </div>
              <div className="text-sm font-medium">
                {formatRelativeTime(siteState?.published_at)}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatDate(siteState?.published_at)}
              </div>
            </div>

            {/* Live Deal Count */}
            <div className="p-3 bg-background rounded-lg border">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Database className="h-3 w-3" />
                Live Deal Count
              </div>
              <div className="text-lg font-bold text-primary">
                {adminLiveDealsCount}
              </div>
              {siteState?.published_deal_count && siteState.published_deal_count !== adminLiveDealsCount && (
                <div className="text-xs text-amber-600">
                  (site_state says {siteState.published_deal_count})
                </div>
              )}
            </div>

            {/* Admin Snapshot */}
            <div className="p-3 bg-background rounded-lg border">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Database className="h-3 w-3" />
                Admin Snapshot
              </div>
              <div className="font-mono text-xs truncate" title={siteState?.admin_snapshot_id || "None"}>
                {siteState?.admin_snapshot_id?.slice(0, 20) || "None"}
              </div>
              <div className="text-xs text-muted-foreground">
                {siteState?.admin_deal_count ?? 0} deals
              </div>
            </div>
          </div>

          {/* Last Publish Audit */}
          {publishHistory.length > 0 && (
            <div className="p-3 bg-background rounded-lg border">
              <div className="text-xs text-muted-foreground mb-2">Last Publish Audit</div>
              <div className="flex items-center gap-3 text-sm">
                <Badge variant="outline" className="text-xs">
                  {publishHistory[0].action}
                </Badge>
                <span className="font-mono text-xs">
                  {publishHistory[0].snapshot_id?.slice(0, 20)}
                </span>
                <span className="text-muted-foreground text-xs">
                  {formatRelativeTime(publishHistory[0].performed_at)}
                </span>
                <span className="text-muted-foreground text-xs">
                  by {publishHistory[0].performed_by || "system"}
                </span>
              </div>
            </div>
          )}

          {/* Policy Info */}
          {siteState && (
            <div className="p-3 bg-background rounded-lg border">
              <div className="text-xs text-muted-foreground mb-2">Deals Retention Policy</div>
              <div className="flex items-center gap-3">
                <Badge variant="outline">
                  Mode: {siteState.public_deals_policy_mode || "normal"}
                </Badge>
                <Badge variant="outline">
                  Grace: {siteState.public_deals_policy_grace_days ?? 7} days
                </Badge>
              </div>
            </div>
          )}

          {/* CR315: Visibility Stats Breakdown */}
          {visibilityStats && (
            <div className="p-3 bg-background rounded-lg border">
              <div className="text-xs text-muted-foreground mb-2">CR315 Visibility Breakdown</div>
              <VisibilityStatsBreakdown 
                stats={visibilityStats} 
                policy={siteState ? {
                  mode: siteState.public_deals_policy_mode,
                  grace_days: siteState.public_deals_policy_grace_days,
                } : null}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onRefresh}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            
            <Button
              size="sm"
              variant="outline"
              onClick={handleCopyDebugJson}
            >
              {copied ? (
                <Check className="h-4 w-4 mr-2 text-green-600" />
              ) : (
                <Copy className="h-4 w-4 mr-2" />
              )}
              Copy Debug JSON
            </Button>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
